import * as React from "react";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  /** Page title text */
  title: string;
  /** Metadata row beneath the title (badges, text, separators) */
  metadata?: React.ReactNode;
  /** Action buttons or controls (displayed on the right) */
  actions?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Reusable page header component for view pages
 * 
 * Layout:
 * - Title on the left (text-2xl font-semibold tracking-tight)
 * - Metadata row directly beneath title (muted text)
 * - Actions aligned to the right (flex gap-2)
 * - Contained in a subtle card-like container
 * 
 * @example
 * <PageHeader
 *   title="Campaign Name"
 *   metadata={
 *     <>
 *       <Badge variant="success">Confirmed</Badge>
 *       <span className="text-muted-foreground">•</span>
 *       <span>SuperValu</span>
 *     </>
 *   }
 *   actions={
 *     <>
 *       <Button variant="ghost">Back to Campaigns</Button>
 *       <Button>Edit Campaign</Button>
 *     </>
 *   }
 * />
 */
export function PageHeader({
  title,
  metadata,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <div
      className={cn(
        "bg-white rounded-xl border p-6 shadow-sm",
        className
      )}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        {/* Left side: Title and metadata */}
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {title}
          </h1>
          {metadata && (
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              {metadata}
            </div>
          )}
        </div>

        {/* Right side: Actions */}
        {actions && (
          <div className="flex flex-shrink-0 items-center gap-2">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Dot separator component for use in metadata rows
 */
export function MetadataSeparator() {
  return <span className="text-muted-foreground">•</span>;
}

export default PageHeader;

